import React, { useState, useEffect, useCallback } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight, Btn, Pill } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n == null ? '—' : Number(n).toLocaleString(); }
function dur(s, e) {
  if (!s) return '—';
  const ms = (e ? new Date(e) : new Date()) - new Date(s);
  if (ms < 60000) return Math.round(ms/1000) + 's';
  if (ms < 3600000) return Math.round(ms/60000) + 'm';
  return (ms/3600000).toFixed(1) + 'h';
}
function timeAgo(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts);
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return Math.round(ms/60000) + 'm ago';
  if (ms < 86400000) return Math.round(ms/3600000) + 'h ago';
  return Math.round(ms/86400000) + 'd ago';
}

const PIPELINES = [
  { id: 'geocode', label: 'Geocoding', desc: 'Adds GPS coordinates using Nominatim (free) → Google Geocoding ($5/1k) → Haiku (~$10/1k)', color: '#e05c5c', priority: 1 },
  { id: 'enrich',  label: 'Enrichment', desc: 'Fills booking URL, specialties URL, and extra details', color: '#d97706', priority: 2 },
  { id: 'verify',  label: 'Verification', desc: 'Confirms names, addresses and contact info are correct', color: '#6F8FA7', priority: 3 },
];
const STATUS_COLOR = { running:'#2da06a', done:'#6F8FA7', failed:'#e05c5c', cancelled:'#9eb8c8', queued:'#d97706', picked:'#2da06a' };
const STATUS_LABEL = { running:'Running', done:'Done', failed:'Failed', cancelled:'Cancelled', queued:'Queued', picked:'Running' };

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setMob(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return mob;
}

export default function Pipeline({ onNav }) {
  const [runs,      setRuns]    = useState([]);
  const [jobs,      setJobs]    = useState([]);
  const [countries, setCtry]    = useState([]);
  const [loading,   setLoading] = useState(true);
  const [toast,     setToast]   = useState(null);
  const [me,        setMe]      = useState('admin');
  // Queue form
  const [qPipeline, setQPipeline] = useState('geocode');
  const [qCountry,  setQCountry]  = useState('');
  const [qPriority, setQPriority] = useState(5);
  const [qStrategy, setQStrategy] = useState('nominatim');
  const [queuing,   setQueuing]   = useState(false);
  const isMobile = useIsMobile();

  function showToast(msg, type) { setToast({ msg, type: type||'ok' }); setTimeout(() => setToast(null), 3000); }

  const load = useCallback(async () => {
    try {
      const [runsRes, jobsRes, ctryRes] = await Promise.all([
        supabase.rpc('admin_get_pipeline_runs', { p_limit: 40 }),
        supabase.rpc('admin_get_pipeline_jobs'),
        supabase.rpc('get_distinct_countries'),
      ]);
      setRuns(runsRes.data || []);
      setJobs(jobsRes.data || []);
      setCtry((ctryRes.data || []).map(r => r.country || r).filter(Boolean));
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.auth.getUser().then(({ data }) => { if (data?.user) setMe(data.user.email?.split('@')[0] || 'admin'); });
    // Poll every 8s if a run is active
    const interval = setInterval(() => {
      if (runs.some(r => r.status === 'running')) load();
    }, 8000);
    return () => clearInterval(interval);
  }, [load, runs]);

  async function handleQueue() {
    setQueuing(true);
    try {
      await supabase.rpc('admin_queue_pipeline_job', {
        p_pipeline: qPipeline, p_country: qCountry || null,
        p_priority: qPriority, p_created_by: me,
        p_strategy: qPipeline === 'geocode' ? qStrategy : null,
      });
      // Log to activity log
      try {
        const stratLabel = qPipeline === 'geocode'
          ? { nominatim: 'Nominatim (free)', google: 'Nominatim → Geocoding', haiku: 'Nominatim → Geocoding → Haiku' }[qStrategy]
          : null;
        const detail = [qCountry || 'all countries', stratLabel].filter(Boolean).join(' · ');
        await supabase.rpc('admin_log_activity', {
          p_action: 'pipeline_queued',
          p_detail: qPipeline + ' · ' + detail,
        });
      } catch (_e) {}
      showToast('Job queued — your local pipeline will pick it up');
      await load();
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
    setQueuing(false);
  }

  async function handleCancel(id) {
    try {
      await supabase.rpc('admin_cancel_pipeline_job', { p_id: id });
      setJobs(j => j.filter(x => x.id !== id));
      showToast('Job cancelled', 'warn');
    } catch (_e) {}
  }

  const runningNow  = runs.filter(r => r.status === 'running');
  const recentRuns  = runs.filter(r => r.status !== 'running');
  const totalDone   = runs.reduce((s,r) => s + (r.succeeded||0), 0);
  const totalFailed = runs.reduce((s,r) => s + (r.failed||0), 0);
  const queueLen    = jobs.length;

  const pct = (a, b) => b ? Math.round(a/b*100) : 0;

  return (
    <div style={{ fontFamily: F }}>
      {toast && <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background:toast.type==='warn'?'#7c3a00':'#1b5e20', color:'#fff', padding:'10px 20px', borderRadius:10, fontSize:'.78rem', fontWeight:500, zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>}

      <Grid cols={4}>
        <MetricCard label="Running now"   value={loading?'…':runningNow.length}  sub={runningNow.length>0?'active pipeline':'idle'} trend={runningNow.length>0?'up':null} />
        <MetricCard label="Queued jobs"   value={loading?'…':queueLen}            sub="waiting for local runner" />
        <MetricCard label="Total processed" value={loading?'…':fmt(totalDone)}   sub="records succeeded" trend={totalDone>0?'up':null} />
        <MetricCard label="Total failed"  value={loading?'…':fmt(totalFailed)}   sub="need re-run" trend={totalFailed>0?'down':null} accent={totalFailed>0?'#e05c5c':undefined} />
      </Grid>

      <Insight type="info">
        The pipeline runs on your <strong>local Windows machine</strong>. Queue a job here — your local pipeline polls Supabase for queued jobs and picks them up automatically. Run <code style={{ background:'#edf6fb', padding:'1px 6px', borderRadius:4, fontSize:'.85em' }}>node scripts/pipeline.js --poll</code> to start listening.
      </Insight>

      {/* Running now */}
      {runningNow.length > 0 && (
        <Card>
          <CardTitle>Running now</CardTitle>
          {runningNow.map(r => {
            const progress = r.total ? pct(r.processed, r.total) : null;
            return (
              <div key={r.id} style={{ background:'#f0fbf5', border:'1px solid #c8eddb', borderRadius:9, padding:'12px 14px', marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:'.8rem', fontWeight:600, color:'#1a3040' }}>{r.pipeline} {r.country ? `· ${r.country}` : '· All countries'}</div>
                    <div style={{ fontSize:'.63rem', color:'#9eb8c8', marginTop:2 }}>Started {timeAgo(r.started_at)} · {dur(r.started_at, null)} elapsed · by {r.triggered_by||'pipeline'}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:'#2da06a', display:'inline-block', animation:'pulse 1.5s infinite' }} />
                    <span style={{ fontSize:'.65rem', color:'#2da06a', fontWeight:600 }}>Live</span>
                  </div>
                </div>
                {progress !== null && (
                  <>
                    <div style={{ height:6, background:'#d8eaf4', borderRadius:999, overflow:'hidden', marginBottom:5 }}>
                      <div style={{ height:'100%', width:progress+'%', background:'#2da06a', borderRadius:999, transition:'width .5s' }} />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.62rem', color:'#6F8FA7' }}>
                      <span>{fmt(r.processed)} / {fmt(r.total)} processed</span>
                      <span>{progress}% · {fmt(r.succeeded)} ok · {fmt(r.failed)} failed</span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {/* Queue + new job */}
      <div style={{ display:isMobile?'block':'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ marginBottom:isMobile?16:0 }}>
          <CardTitle>Queue a pipeline job</CardTitle>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:'.63rem', color:'#6F8FA7', marginBottom:4 }}>Pipeline</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {PIPELINES.map(p => (
                <button key={p.id} onClick={() => setQPipeline(p.id)}
                  style={{ padding:'5px 12px', fontSize:'.68rem', fontWeight:qPipeline===p.id?600:400, background:qPipeline===p.id?p.color:'#f5fafc', color:qPipeline===p.id?'#fff':'#6F8FA7', border:'1px solid '+(qPipeline===p.id?p.color:'#d8eaf4'), borderRadius:7, cursor:'pointer', fontFamily:F }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize:'.61rem', color:'#9eb8c8', marginTop:5 }}>{PIPELINES.find(p=>p.id===qPipeline)?.desc}</div>
          </div>
          {qPipeline === 'geocode' && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:'.63rem', color:'#6F8FA7', marginBottom:4 }}>Strategy</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {[
                  { id:'nominatim', label:'Nominatim only', sub:'Free' },
                  { id:'google',    label:'+ Google',       sub:'$5/1k fallback' },
                  { id:'haiku',     label:'+ Haiku',        sub:'~$10/1k fallback' },
                ].map(s => (
                  <button key={s.id} onClick={() => setQStrategy(s.id)}
                    style={{ padding:'5px 12px', fontSize:'.68rem', fontWeight: qStrategy===s.id ? 600 : 400,
                      background: qStrategy===s.id ? '#314F5C' : '#f5fafc',
                      color: qStrategy===s.id ? '#AFE1F6' : '#6F8FA7',
                      border:'1px solid '+(qStrategy===s.id ? '#314F5C' : '#d8eaf4'),
                      borderRadius:7, cursor:'pointer', fontFamily:F }}>
                    {s.label} <span style={{ fontSize:'.58rem', opacity:.7 }}>{s.sub}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:'.63rem', color:'#6F8FA7', marginBottom:3 }}>Country (leave empty for all countries)</div>
            <select value={qCountry} onChange={e => setQCountry(e.target.value)}
              style={{ fontSize:'.72rem', border:'1px solid #d8eaf4', borderRadius:7, padding:'7px 10px', color:'#1a3040', fontFamily:F, width:'100%' }}>
              <option value="">All countries</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:'.63rem', color:'#6F8FA7', marginBottom:3 }}>Priority (1 = highest, 10 = lowest)</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="range" min={1} max={10} value={qPriority} onChange={e => setQPriority(Number(e.target.value))} style={{ flex:1 }} />
              <span style={{ fontSize:'.78rem', fontWeight:600, color:'#314F5C', minWidth:16 }}>{qPriority}</span>
            </div>
          </div>
          <Btn onClick={handleQueue} disabled={queuing}>{queuing ? 'Queuing…' : 'Queue job'}</Btn>
        </Card>

        <Card style={{ marginBottom:0 }}>
          <CardTitle>Pending jobs ({queueLen})</CardTitle>
          {loading ? <div style={{ color:'#9eb8c8', fontSize:'.75rem' }}>Loading…</div>
          : jobs.length === 0 ? <Insight type="ok">No jobs queued. Your local pipeline is idle.</Insight>
          : jobs.map(j => (
            <div key={j.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'.5px solid rgba(49,79,92,.06)' }}>
              <div>
                <div style={{ fontSize:'.72rem', color:'#1a3040', fontWeight:500 }}>{j.pipeline} {j.country ? `· ${j.country}` : '· All'}</div>
                <div style={{ fontSize:'.61rem', color:'#9eb8c8', marginTop:1 }}>Priority {j.priority} · {timeAgo(j.created_at)} · {j.created_by}</div>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <span style={{ fontSize:'.6rem', padding:'2px 7px', borderRadius:6, background:(STATUS_COLOR[j.status]||'#9eb8c8')+'18', color:STATUS_COLOR[j.status]||'#9eb8c8', fontWeight:600 }}>{STATUS_LABEL[j.status]||j.status}</span>
                {j.status === 'queued' && <button onClick={() => handleCancel(j.id)} style={{ padding:'2px 8px', background:'#fef0ef', color:'#c0392b', border:'none', borderRadius:5, fontSize:'.62rem', cursor:'pointer', fontFamily:F }}>Cancel</button>}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* Run history */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <CardTitle>Run history</CardTitle>
          <button onClick={load} style={{ fontSize:'.65rem', color:'#6F8FA7', background:'none', border:'none', cursor:'pointer', textDecoration:'underline', fontFamily:F }}>↻ Refresh</button>
        </div>
        {loading ? <div style={{ color:'#9eb8c8', fontSize:'.75rem' }}>Loading…</div>
        : recentRuns.length === 0 ? <Insight type="info">No completed runs yet. Queue a job and run your local pipeline to see history here.</Insight>
        : (
          <div style={{ overflowX:'auto' }}>
            <div style={{ minWidth:560 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr .8fr .9fr .6fr .6fr .6fr .6fr .7fr', gap:6, paddingBottom:7, borderBottom:'1px solid #edf6fb', marginBottom:4 }}>
                {['Pipeline','Country','Started','Duration','Total','OK','Failed','Status'].map(h => (
                  <span key={h} style={{ fontSize:'.57rem', color:'#9eb8c8', textTransform:'uppercase', letterSpacing:'.06em' }}>{h}</span>
                ))}
              </div>
              {recentRuns.map(r => {
                const clr = STATUS_COLOR[r.status] || '#9eb8c8';
                return (
                  <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr .8fr .9fr .6fr .6fr .6fr .6fr .7fr', gap:6, padding:'6px 0', borderBottom:'.5px solid rgba(49,79,92,.06)', alignItems:'center' }}>
                    <span style={{ fontSize:'.72rem', color:'#1a3040', fontWeight:500 }}>{r.pipeline}</span>
                    <span style={{ fontSize:'.68rem', color:'#6F8FA7' }}>{r.country||'All'}</span>
                    <span style={{ fontSize:'.65rem', color:'#9eb8c8' }}>{timeAgo(r.started_at)}</span>
                    <span style={{ fontSize:'.68rem', color:'#6F8FA7' }}>{dur(r.started_at, r.finished_at)}</span>
                    <span style={{ fontSize:'.68rem', color:'#6F8FA7', fontVariantNumeric:'tabular-nums' }}>{fmt(r.total)}</span>
                    <span style={{ fontSize:'.68rem', color:'#2da06a', fontVariantNumeric:'tabular-nums' }}>{fmt(r.succeeded)}</span>
                    <span style={{ fontSize:'.68rem', color:r.failed>0?'#e05c5c':'#9eb8c8', fontVariantNumeric:'tabular-nums' }}>{fmt(r.failed)}</span>
                    <span style={{ fontSize:'.62rem', padding:'2px 7px', borderRadius:6, background:clr+'18', color:clr, fontWeight:600 }}>{STATUS_LABEL[r.status]||r.status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* How to connect the pipeline */}
      <Card>
        <CardTitle>Connecting your local pipeline</CardTitle>
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:12 }}>
          {[
            { step:'1', label:'Add Supabase env vars', desc:'Add SUPABASE_URL and SUPABASE_SERVICE_KEY to your pipeline .env so it can read pipeline_jobs and write pipeline_runs.' },
            { step:'2', label:'Run in poll mode', desc:'node scripts/pipeline.js --poll — the pipeline checks for queued jobs every 30s, picks the highest priority one, and updates run status in real time.' },
            { step:'3', label:'Queue from here', desc:'Use the form above to queue geocoding, enrichment, or verification jobs. Set country and priority. The pipeline will pick them up in order.' },
            { step:'4', label:'Monitor live', desc:'This screen polls every 8 seconds when a run is active. Progress bars, success/failure counts and duration all update live from Supabase.' },
          ].map(({ step, label, desc }) => (
            <div key={step} style={{ background:'#f5fafc', borderRadius:8, padding:'10px 14px', display:'flex', gap:10 }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'#314F5C', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.65rem', fontWeight:700, flexShrink:0, marginTop:1 }}>{step}</div>
              <div>
                <div style={{ fontSize:'.72rem', fontWeight:600, color:'#314F5C', marginBottom:2 }}>{label}</div>
                <div style={{ fontSize:'.64rem', color:'#9eb8c8', lineHeight:1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
